// Professional Color Themes for Resume Templates
// These can be applied to any template layout

export const colorThemes = [
  {
    id: 'minimal-professional',
    name: 'Minimal Professional',
    description: 'Tech, corporate, consulting, finance',
    colors: {
      primary: '#0077B6',
      secondary: '#555555',
      accent: '#0077B6',
      text: '#222222',
      light: '#FFFFFF',
      sidebar: '#EAEAEA'
    }
  },
  {
    id: 'soft-pastel',
    name: 'Soft Pastel Elegance',
    description: 'Design, marketing, HR, education',
    colors: {
      primary: '#3C3C3C',
      secondary: '#4A5568',
      accent: '#A1C4FD',
      text: '#3C3C3C',
      light: '#FAFAFA',
      sidebar: '#C2E9FB'
    }
  },
  {
    id: 'monochrome-classic',
    name: 'Monochrome Classic',
    description: 'Legal, academic, senior corporate roles',
    colors: {
      primary: '#000000',
      secondary: '#555555',
      accent: '#555555',
      text: '#000000',
      light: '#FFFFFF',
      sidebar: '#DDDDDD'
    }
  },
  {
    id: 'warm-creative',
    name: 'Warm Creative',
    description: 'Startups, marketing, design, freelancers',
    colors: {
      primary: '#FF8C42',
      secondary: '#2D3436',
      accent: '#00B4D8',
      text: '#2D3436',
      light: '#FFFFFF',
      sidebar: '#FFE156'
    }
  },
  {
    id: 'modern-blue',
    name: 'Modern Blue',
    description: 'Engineers, developers, business analysts',
    colors: {
      primary: '#2563EB',
      secondary: '#1E293B',
      accent: '#38BDF8',
      text: '#1E293B',
      light: '#F9FAFB',
      sidebar: '#CBD5E1'
    }
  },
  {
    id: 'nature-calm',
    name: 'Nature-Inspired Calm',
    description: 'Wellness, HR, sustainability, educators',
    colors: {
      primary: '#A7C957',
      secondary: '#4B5563',
      accent: '#A7C957',
      text: '#1E3932',
      light: '#F8F9F9',
      sidebar: '#F2E8CF'
    }
  },
  {
    id: 'sleek-dark',
    name: 'Sleek Dark Mode',
    description: 'Developers, product designers, AI/tech startups',
    colors: {
      primary: '#6C63FF',
      secondary: '#B0B0B0',
      accent: '#BB86FC',
      text: '#EAEAEA',
      light: '#1E1E1E',
      sidebar: '#2D2D2D'
    }
  },
  {
    id: 'royal-indigo',
    name: 'Royal Indigo',
    description: 'Product, design, modern tech roles',
    colors: {
      primary: '#4F46E5',
      secondary: '#475569',
      accent: '#818CF8',
      text: '#1E1B4B',
      light: '#EEF2FF',
      sidebar: '#E0E7FF'
    }
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    description: 'Finance, operations, environmental roles',
    colors: {
      primary: '#166534',
      secondary: '#4B5563',
      accent: '#22C55E',
      text: '#14261B',
      light: '#F0FDF4',
      sidebar: '#DCFCE7'
    }
  },
  {
    id: 'burgundy-executive',
    name: 'Burgundy Executive',
    description: 'Executives, law, senior management',
    colors: {
      primary: '#7F1D1D',
      secondary: '#57534E',
      accent: '#B91C1C',
      text: '#292524',
      light: '#FEF2F2',
      sidebar: '#FEE2E2'
    }
  }
];

// Apply color theme to HTML string
export function applyColorThemeToHtml(html, colorTheme) {
  if (!html || !colorTheme || !colorTheme.colors) {
    return html;
  }

  const { colors } = colorTheme;
  
  // Replace color values in the HTML using regex
  // This finds CSS color declarations and replaces them
  let styledHtml = html;

  // Replace in style tags
  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  styledHtml = styledHtml.replace(styleRegex, (match, styleContent) => {
    let updatedStyle = styleContent;
    
    // Replace color values - we need to be careful to replace exact hex values
    // Primary color
    updatedStyle = updatedStyle.replace(/color:\s*#[0-9A-Fa-f]{6}\s*;/gi, (colorMatch) => {
      // Only replace if it's in a property that uses primary color patterns
      return colorMatch;
    });
    
    // This is a simpler approach: replace known color variables
    // We'll use a more reliable method with CSS custom properties
    return `<style>${updatedStyle}</style>`;
  });

  // Better approach: inject CSS custom properties at the top of the style block
  styledHtml = styledHtml.replace(/<style[^>]*>/, (match) => {
    return `${match}
    :root {
      --color-primary: ${colors.primary};
      --color-secondary: ${colors.secondary};
      --color-accent: ${colors.accent};
      --color-text: ${colors.text};
      --color-light: ${colors.light};
      --color-sidebar: ${colors.sidebar || colors.light};
    }`;
  });

  // Replace hardcoded color values with references to the actual colors
  // This is a more direct approach - replace specific hex values
  const colorMappings = [
    { old: /#0077B6/gi, new: colors.primary },
    { old: /#555555/gi, new: colors.secondary },
    { old: /#222222/gi, new: colors.text },
    { old: /#FFFFFF/gi, new: colors.light },
    { old: /#EAEAEA/gi, new: colors.sidebar || colors.light },
    // Modern Blue
    { old: /#2563EB/gi, new: colors.primary },
    { old: /#1E293B/gi, new: colors.secondary },
    { old: /#38BDF8/gi, new: colors.accent },
    { old: /#F9FAFB/gi, new: colors.light },
    { old: /#CBD5E1/gi, new: colors.sidebar || colors.light },
    // Soft Pastel
    { old: /#3C3C3C/gi, new: colors.text },
    { old: /#4A5568/gi, new: colors.secondary },
    { old: /#A1C4FD/gi, new: colors.accent },
    { old: /#FAFAFA/gi, new: colors.light },
    { old: /#C2E9FB/gi, new: colors.sidebar || colors.light },
    // Monochrome
    { old: /#000000/gi, new: colors.text },
    { old: /#DDDDDD/gi, new: colors.sidebar || colors.light },
    // Nature Calm
    { old: /#A7C957/gi, new: colors.primary },
    { old: /#4B5563/gi, new: colors.secondary },
    { old: /#1E3932/gi, new: colors.text },
    { old: /#F8F9F9/gi, new: colors.light },
    { old: /#F2E8CF/gi, new: colors.sidebar || colors.light },
    // Warm Creative
    { old: /#FF8C42/gi, new: colors.primary },
    { old: /#2D3436/gi, new: colors.secondary },
    { old: /#00B4D8/gi, new: colors.accent },
    { old: /#FFE156/gi, new: colors.sidebar || colors.light },
    // Dark Mode
    { old: /#6C63FF/gi, new: colors.primary },
    { old: /#B0B0B0/gi, new: colors.secondary },
    { old: /#BB86FC/gi, new: colors.accent },
    { old: /#EAEAEA/gi, new: colors.light },
    { old: /#1E1E1E/gi, new: colors.text },
    { old: /#2D2D2D/gi, new: colors.sidebar || colors.light },
    // Royal Indigo
    { old: /#4F46E5/gi, new: colors.primary },
    { old: /#475569/gi, new: colors.secondary },
    { old: /#818CF8/gi, new: colors.accent },
    { old: /#1E1B4B/gi, new: colors.text },
    { old: /#EEF2FF/gi, new: colors.light },
    { old: /#E0E7FF/gi, new: colors.sidebar || colors.light },
    // Forest Green
    { old: /#166534/gi, new: colors.primary },
    { old: /#22C55E/gi, new: colors.accent },
    { old: /#14261B/gi, new: colors.text },
    { old: /#F0FDF4/gi, new: colors.light },
    { old: /#DCFCE7/gi, new: colors.sidebar || colors.light },
    // Burgundy Executive
    { old: /#7F1D1D/gi, new: colors.primary },
    { old: /#57534E/gi, new: colors.secondary },
    { old: /#B91C1C/gi, new: colors.accent },
    { old: /#292524/gi, new: colors.text },
    { old: /#FEF2F2/gi, new: colors.light },
    { old: /#FEE2E2/gi, new: colors.sidebar || colors.light }
  ];

  // Apply all color replacements within style tags only
  styledHtml = styledHtml.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, styleContent) => {
    let updatedStyle = styleContent;
    colorMappings.forEach(mapping => {
      updatedStyle = updatedStyle.replace(mapping.old, mapping.new);
    });
    return `<style>${updatedStyle}</style>`;
  });

  return styledHtml;
}

